/**
 * Production-Ready Error Monitoring System V2
 * 
 * Features:
 * - Circuit Breaker Pattern (5 failures in 60s)
 * - Tiered Rate Limiting (20/50/100)
 * - URL Exclusion at Capture Phase
 * - TTL-based Memory Cleanup
 * - Sliding Window Algorithm
 * - Controlled Initialization
 * - Breadcrumb tracking for error context
 * - Component tree parsing from ErrorBoundary
 * 
 * CRITICAL: URL exclusion happens at CAPTURE phase to prevent loops
 */

// @ts-nocheck
import { breadcrumbTracker } from '@/lib/monitoring/breadcrumbs'
import { parseComponentStack } from '@/lib/monitoring/component-stack-parser'

type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical'
type ErrorCategory = 
  | 'javascript_error'
  | 'api_error'
  | 'authentication_error'
  | 'payment_error'
  | 'ai_generation_error'
  | 'database_error'
  | 'validation_error'
  | 'rate_limit_error'
  | 'security_violation'
  | 'performance_issue'
  | 'user_reported'
  | 'unhandled_rejection'
  | 'network_error'
  | 'error_reporting' // Excluded category
  | 'unknown'

interface ErrorReport {
  message: string
  stack?: string
  category: ErrorCategory
  severity: ErrorSeverity
  source: 'client' | 'server' | 'middleware' | 'external'
  timestamp: string
  url: string
  user_agent?: string
  user_id?: string
  story_id?: string
  session_id?: string
  component?: string
  operation?: string
  api_endpoint?: string
  status_code?: number
  userTier?: 'basic' | 'premium'
  custom_data?: Record<string, unknown>
  response_time?: number
  fingerprint?: string
  error_type?: string
}

interface CircuitBreakerState {
  state: 'closed' | 'open' | 'half-open'
  failureCount: number
  lastFailureTime: number
  testRequestCount: number
  nextRetryTime: number
}

interface RateLimitTier {
  maxRequests: number
  windowMs: number
  action: 'continue' | 'queue' | 'console'
}

interface DeduplicationEntry {
  count: number
  firstSeen: number
  lastSeen: number
}

interface ObservabilityCallbacks {
  onCircuitOpen?: (failureCount: number) => void
  onCircuitClose?: () => void
  onRateLimitHit?: (fingerprint: string, tier: number) => void
  onErrorDropped?: (reason: string, error: ErrorReport) => void
  onQueueFull?: (queueSize: number) => void
}

class ErrorMonitorV2 {
  // Circuit Breaker Configuration
  private readonly CIRCUIT_BREAKER = {
    failureThreshold: 5,
    timeWindow: 60000, // 60 seconds
    cooldownPeriod: 60000, // 60 seconds
    maxTestRequests: 1,
    backoffDelays: [1000, 2000, 4000, 8000, 16000, 32000] // Exponential backoff
  }

  // Rate Limiting Configuration (Tiered)
  private readonly RATE_LIMITS: RateLimitTier[] = [
    { maxRequests: 20, windowMs: 60000, action: 'continue' },    // Tier 1: 20/min
    { maxRequests: 50, windowMs: 300000, action: 'queue' },       // Tier 2: 50/5min
    { maxRequests: 100, windowMs: 300000, action: 'console' }      // Tier 3: 100/5min
  ]

  // Deduplication Configuration
  private readonly DEDUPLICATION = {
    windowMs: 300000, // 5 minutes
    maxCacheSize: 1000
  }

  // Queue Configuration
  private readonly QUEUE = {
    maxSize: 100,
    strategy: 'dropOldest' as const
  }

  // State Management
  private circuitBreaker: CircuitBreakerState
  private rateLimitStore: Map<string, number[]> = new Map()
  private deduplicationStore: Map<string, DeduplicationEntry> = new Map()
  private errorQueue: ErrorReport[] = []
  private cleanupInterval: NodeJS.Timeout | null = null
  private drainInterval: NodeJS.Timeout | null = null
  private isInitialized: boolean = false
  private isDestroyed: boolean = false
  private sessionId: string
  private userId?: string
  private callbacks: ObservabilityCallbacks = {}

  // Global Error Handlers (bound to preserve context)
  private globalErrorHandler: (event: ErrorEvent) => void
  private globalRejectionHandler: (event: PromiseRejectionEvent) => void

  constructor() {
    this.circuitBreaker = {
      state: 'closed',
      failureCount: 0,
      lastFailureTime: 0,
      testRequestCount: 0,
      nextRetryTime: 0
    }

    this.sessionId = this.generateSessionId()
    
    // Bind handlers to preserve context
    this.globalErrorHandler = this.handleGlobalError.bind(this)
    this.globalRejectionHandler = this.handleGlobalRejection.bind(this)
  }

  /**
   * Initialize the error monitoring system
   * CRITICAL: Only call this when ready, not in constructor
   */
  init(callbacks?: ObservabilityCallbacks): void {
    if (this.isInitialized || this.isDestroyed) {
      this.log('warn', 'Attempted to initialize already initialized/destroyed ErrorMonitorV2')
      return
    }

    if (typeof window === 'undefined') {
      this.log('warn', 'ErrorMonitorV2 can only be initialized on client-side')
      return
    }

    try {
      this.callbacks = callbacks || {}
      this.setupGlobalErrorHandlers()
      this.attachToWindow()
      this.startCleanupInterval()
      this.ensureQueueProcessor()
      this.isInitialized = true
      
      this.log('info', 'ErrorMonitorV2 initialized successfully')
    } catch (error) {
      this.log('error', 'ErrorMonitorV2 initialization failed', error)
      this.destroy()
    }
  }

  /**
   * Destroy the error monitoring system
   * Removes all handlers and cleans up resources
   */
  destroy(): void {
    if (this.isDestroyed) return

    try {
      // Remove global handlers
      if (typeof window !== 'undefined') {
        window.removeEventListener('error', this.globalErrorHandler)
        window.removeEventListener('unhandledrejection', this.globalRejectionHandler)
      }

      // Clear intervals
      if (this.cleanupInterval) {
        clearInterval(this.cleanupInterval)
        this.cleanupInterval = null
      }

      if (this.drainInterval) {
        clearInterval(this.drainInterval)
        this.drainInterval = null
      }

      // Clear stores
      this.rateLimitStore.clear()
      this.deduplicationStore.clear()
      this.errorQueue = []

      // Reset state
      this.isInitialized = false
      this.isDestroyed = true
      this.callbacks = {}

      this.log('info', 'ErrorMonitorV2 destroyed successfully')
    } catch (error) {
      console.error('Error destroying ErrorMonitorV2:', error)
    }
  }

  /**
   * CRITICAL: Check URL exclusion at CAPTURE phase
   * This prevents infinite loops by blocking error reporting failures
   */
  private shouldExcludeAtCapture(event: ErrorEvent | PromiseRejectionEvent): boolean {
    // Check filename/URL for error reporting endpoints
    if ('filename' in event && event.filename) {
      if (event.filename.includes('/api/errors') || 
          event.filename.includes('/api/health')) {
        return true
      }
    }

    // Check error message content
    const message = 'message' in event ? event.message : 
                   'reason' in event ? String(event.reason) : ''
    
    if (message.includes('error reporting') ||
        message.includes('Failed to send error report') ||
        message.includes('Error reporting failed')) {
      return true
    }

    // Check stack trace
    const error = 'error' in event ? event.error : 
                  'reason' in event ? event.reason : null
    
    if (error && typeof error === 'object' && 'stack' in error) {
      const stack = String(error.stack)
      if (stack.includes('/api/errors') || 
          stack.includes('sendErrorReport') ||
          stack.includes('captureError')) {
        return true
      }
    }

    return false
  }

  /**
   * Global error handler with URL exclusion at capture phase
   */
  private handleGlobalError(event: ErrorEvent): void {
    // CRITICAL: Check exclusion at capture phase
    if (this.shouldExcludeAtCapture(event)) {
      this.log('debug', 'Excluded error at capture phase', { filename: event.filename, message: event.message })
      return
    }

    this.captureError({
      message: event.message || 'Unknown JavaScript error',
      stack: event.error?.stack,
      category: 'javascript_error',
      severity: 'high',
      source: 'client',
      url: event.filename || window.location.href,
      custom_data: {
        lineno: event.lineno,
        colno: event.colno,
        filename: event.filename
      }
    })
  }

  /**
   * Global rejection handler with URL exclusion at capture phase
   */
  private handleGlobalRejection(event: PromiseRejectionEvent): void {
    // CRITICAL: Check exclusion at capture phase
    if (this.shouldExcludeAtCapture(event)) {
      this.log('debug', 'Excluded rejection at capture phase', { reason: event.reason })
      return
    }

    this.captureError({
      message: event.reason?.message || String(event.reason) || 'Unhandled promise rejection',
      stack: event.reason?.stack,
      category: 'unhandled_rejection',
      severity: 'high',
      source: 'client',
      url: window.location.href,
      custom_data: { reason: event.reason }
    })
  }

  /**
   * Circuit breaker check
   */
  private checkCircuitBreaker(): boolean {
    const now = Date.now()
    
    if (this.circuitBreaker.state === 'open') {
      if (now >= this.circuitBreaker.nextRetryTime) {
        this.circuitBreaker.state = 'half-open'
        this.circuitBreaker.testRequestCount = 0
        this.log('info', 'Circuit breaker moved to half-open state')
        this.callbacks.onCircuitClose?.()
      } else {
        return false // Circuit is open
      }
    }
    
    if (this.circuitBreaker.state === 'half-open') {
      if (this.circuitBreaker.testRequestCount >= this.CIRCUIT_BREAKER.maxTestRequests) {
        return false // Max test requests reached
      }
    }
    
    return true
  }

  /**
   * Record circuit breaker failure
   */
  private recordCircuitFailure(): void {
    const now = Date.now()
    this.circuitBreaker.failureCount++
    this.circuitBreaker.lastFailureTime = now
    
    if (this.circuitBreaker.failureCount >= this.CIRCUIT_BREAKER.failureThreshold) {
      this.circuitBreaker.state = 'open'
      const backoffIndex = Math.min(this.circuitBreaker.failureCount - this.CIRCUIT_BREAKER.failureThreshold, 
                                   this.CIRCUIT_BREAKER.backoffDelays.length - 1)
      const backoffDelay = this.CIRCUIT_BREAKER.backoffDelays[backoffIndex] ?? 32000 // Fallback to max delay
      this.circuitBreaker.nextRetryTime = now + backoffDelay
      
      this.log('warn', 'Circuit breaker opened', { 
        failureCount: this.circuitBreaker.failureCount,
        nextRetryTime: this.circuitBreaker.nextRetryTime 
      })
      this.callbacks.onCircuitOpen?.(this.circuitBreaker.failureCount)
    }
  }

  /**
   * Record circuit breaker success
   */
  private recordCircuitSuccess(): void {
    this.circuitBreaker.failureCount = 0
    this.circuitBreaker.state = 'closed'
    this.circuitBreaker.testRequestCount = 0
    this.log('info', 'Circuit breaker closed')
    this.callbacks.onCircuitClose?.()
  }

  /**
   * Tiered rate limiting with sliding window
   */
  private checkRateLimit(fingerprint: string): { allowed: boolean; tier: number; action: string } {
    const now = Date.now()
    
    for (let tierIndex = 0; tierIndex < this.RATE_LIMITS.length; tierIndex++) {
      const tier = this.RATE_LIMITS[tierIndex]
      if (!tier) continue // Skip if tier doesn't exist
      
      const requests = this.rateLimitStore.get(fingerprint) || []
      
      // Remove old requests outside window
      const validRequests = requests.filter(time => now - time < tier.windowMs)
      
      if (validRequests.length >= tier.maxRequests) {
        this.log('warn', 'Rate limit hit', { fingerprint, tier: tierIndex + 1, action: tier.action })
        this.callbacks.onRateLimitHit?.(fingerprint, tierIndex + 1)
        
        return { allowed: false, tier: tierIndex + 1, action: tier.action }
      }
    }
    
    // Record this request for all tiers
    const requests = this.rateLimitStore.get(fingerprint) || []
    requests.push(now)
    this.rateLimitStore.set(fingerprint, requests)
    
    return { allowed: true, tier: 0, action: 'continue' }
  }

  /**
   * Deduplication check
   */
  private checkDeduplication(fingerprint: string): boolean {
    const now = Date.now()
    const entry = this.deduplicationStore.get(fingerprint)
    
    if (entry) {
      if (now - entry.lastSeen < this.DEDUPLICATION.windowMs) {
        entry.count++
        entry.lastSeen = now
        return false // Duplicate within window
      }
    }
    
    // New or expired entry
    this.deduplicationStore.set(fingerprint, {
      count: 1,
      firstSeen: now,
      lastSeen: now
    })
    
    return true
  }

  /**
   * Generate error fingerprint for deduplication
   */
  private generateFingerprint(errorReport: ErrorReport): string {
    const components = [
      errorReport.message,
      errorReport.category,
      errorReport.source,
      errorReport.url,
      errorReport.component
    ].filter(Boolean).join('|')
    
    let hash = 0
    for (let i = 0; i < components.length; i++) {
      const char = components.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    
    return Math.abs(hash).toString(36).substring(0, 8)
  }

  /**
   * Detect error type for specialized handling
   */
  private detectErrorType(error: { message?: string; stack?: string }): string {
    const msg = (error.message || '').toLowerCase()
    const stack = (error.stack || '').toLowerCase()
    
    // React #185: Hook count mismatch
    if (msg.includes('185') ||
        msg.includes('rendered more hooks') ||
        msg.includes('fewer hooks') ||
        msg.includes('hook order') ||
        (stack.includes('react-dom') && msg.includes('hook'))) {
      return 'REACT_185_HOOK_MISMATCH'
    }
    
    // Future: Add more error types
    // if (msg.includes('hydration')) return 'REACT_HYDRATION_MISMATCH'
    // if (msg.includes('network')) return 'NETWORK_ERROR'
    
    return 'UNKNOWN'
  }

  /**
   * Main error capture method
   */
  captureError(partialError: Omit<ErrorReport, 'timestamp' | 'session_id' | 'user_agent' | 'story_id'>): void {
    console.log('[captureError] Called with:', partialError)
    console.log('[captureError] Breadcrumbs enabled:', breadcrumbTracker.isEnabled())
    console.log('[captureError] Breadcrumbs:', breadcrumbTracker.getAll())

    if (!this.isInitialized || this.isDestroyed) {
      this.log('warn', 'ErrorMonitorV2 not initialized, dropping error')
      return
    }

    if (typeof window === 'undefined') return

    // Detect error type
    const errorType = this.detectErrorType(partialError)

    const currentStoryId = this.getCurrentStoryId()
    const currentUserId = this.getUserId()  // Synchronous, like getCurrentStoryId
    
    // Parse component stack if present
    const componentStackData = partialError.custom_data?.['componentStack']
      ? parseComponentStack(partialError.custom_data['componentStack'] as string)
      : null

    // Get breadcrumbs (only if enabled)
    const breadcrumbs = breadcrumbTracker.isEnabled() 
      ? breadcrumbTracker.getAll() 
      : undefined

    // Add error-type-specific context
    let typeSpecificContext = {}
    if (errorType === 'REACT_185_HOOK_MISMATCH') {
      typeSpecificContext = {
        react185Context: {
          errorName: 'React Hook Count Mismatch',
          knownCauses: [
            'Variable-length array.map() creating components with hooks',
            'Conditional hooks (if statement before useState/useEffect)',
            'Early return before all hooks are called'
          ],
          diagnosticHint: 'Check for arrays that change length when data loads',
          checkFor: [
            'Arrays starting empty [] then filling with data',
            'Optional arrays with .map() creating components',
            'Preferences or data that loads asynchronously'
          ],
          commonPatterns: [
            'availableSubgenres?.map() where array is [] then fills',
            'genreSlots.map() where length changes',
            'if (data) { useState() } - conditional hook'
          ]
        }
      }
    }

    const errorReport: ErrorReport = {
      ...partialError,
      timestamp: new Date().toISOString(),
      url: partialError.url || window.location.href,
      user_agent: navigator.userAgent,
      ...(currentUserId && { user_id: currentUserId }),
      ...(currentStoryId && { story_id: currentStoryId }),
      session_id: this.sessionId,
      error_type: errorType,
      // Enhanced custom_data with breadcrumbs, parsed component tree, deviceInfo, and deployment context
      custom_data: {
        ...(partialError.custom_data || {}),
        deviceInfo: this.getDeviceInfo(),
        deploymentContext: this.getDeploymentContext(),
        ...typeSpecificContext,
        ...(componentStackData && {
          componentTree: componentStackData.componentTree,
          rawComponentStack: componentStackData.rawComponentStack
        }),
        ...(breadcrumbs && breadcrumbs.length > 0 && { breadcrumbs })
      }
    }

    // Generate fingerprint
    const fingerprint = this.generateFingerprint(errorReport)
    errorReport.fingerprint = fingerprint

    // Check deduplication
    if (!this.checkDeduplication(fingerprint)) {
      this.log('debug', 'Duplicate error dropped', { fingerprint })
      return
    }

    // Check rate limiting
    const rateLimitResult = this.checkRateLimit(fingerprint)
    if (!rateLimitResult.allowed) {
      if (rateLimitResult.action === 'queue') {
        this.queueError(errorReport)
      } else if (rateLimitResult.action === 'console') {
        this.log('warn', 'Error dropped due to rate limit', { fingerprint, tier: rateLimitResult.tier })
        this.callbacks.onErrorDropped?.('rate_limit', errorReport)
      }
      return
    }

    // Check circuit breaker
    if (!this.checkCircuitBreaker()) {
      this.queueError(errorReport)
      return
    }

    // Send error report
    this.sendErrorReport(errorReport)
  }

  /**
   * Queue error for later processing
   */
  private queueError(errorReport: ErrorReport): void {
    if (this.errorQueue.length >= this.QUEUE.maxSize) {
      if (this.QUEUE.strategy === 'dropOldest') {
        this.errorQueue.shift() // Remove oldest
      } else {
        this.log('warn', 'Error queue full, dropping error', { queueSize: this.errorQueue.length })
        this.callbacks.onQueueFull?.(this.errorQueue.length)
        this.callbacks.onErrorDropped?.('queue_full', errorReport)
        return
      }
    }

    this.errorQueue.push(errorReport)
    this.log('debug', 'Error queued', { queueSize: this.errorQueue.length })
    this.ensureQueueProcessor()
  }

  /**
   * Process error queue
   */
  private processQueue(): void {
    if (this.errorQueue.length === 0) return

    const error = this.errorQueue.shift()
    if (error) {
      this.captureError(error)
    }

    // Continue processing if queue has more items
    if (this.errorQueue.length > 0) {
      setTimeout(() => this.processQueue(), 1000) // Process next after 1 second
    }
  }

  private ensureQueueProcessor(): void {
    if (typeof window === 'undefined') {
      return
    }

    if (this.drainInterval) {
      return
    }

    this.drainInterval = setInterval(() => {
      if (this.errorQueue.length === 0) {
        return
      }

      this.processQueue()
    }, 5000)
  }

  /**
   * Send error report with circuit breaker tracking
   */
  private async sendErrorReport(errorReport: ErrorReport): Promise<void> {
    if (this.circuitBreaker.state === 'half-open') {
      this.circuitBreaker.testRequestCount++
    }

    try {
      const response = await fetch('/api/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorReport)
      })

      if (response.ok) {
        this.recordCircuitSuccess()
        this.log('debug', 'Error report sent successfully', { fingerprint: errorReport.fingerprint })
      } else {
        this.recordCircuitFailure()
        this.log('warn', 'Error report failed', { status: response.status, fingerprint: errorReport.fingerprint })
      }
    } catch (error) {
      this.recordCircuitFailure()
      this.log('warn', 'Error report network failure', { error, fingerprint: errorReport.fingerprint })
    }
  }

  /**
   * Setup global error handlers
   */
  private setupGlobalErrorHandlers(): void {
    if (typeof window === 'undefined') return

    window.addEventListener('error', this.globalErrorHandler)
    window.addEventListener('unhandledrejection', this.globalRejectionHandler)
  }

  /**
   * Attach to window object
   */
  private attachToWindow(): void {
    if (typeof window === 'undefined') return

    interface WindowWithErrorMonitor extends Window {
      ErrorMonitor?: ErrorMonitorV2
      ErrorMonitorV2?: ErrorMonitorV2
    }
    ;(window as WindowWithErrorMonitor).ErrorMonitor = this
    ;(window as WindowWithErrorMonitor).ErrorMonitorV2 = this
  }

  /**
   * Start cleanup interval for memory management
   */
  private startCleanupInterval(): void {
    this.cleanupInterval = setInterval(() => {
      this.cleanupStores()
    }, 300000) // Every 5 minutes
  }

  /**
   * Cleanup old entries from stores
   */
  private cleanupStores(): void {
    const now = Date.now()
    let cleaned = 0

    // Cleanup rate limit store
    for (const [fingerprint, timestamps] of this.rateLimitStore) {
      const validTimestamps = timestamps.filter(time => now - time < 300000) // 5 minutes
      if (validTimestamps.length === 0) {
        this.rateLimitStore.delete(fingerprint)
        cleaned++
      } else {
        this.rateLimitStore.set(fingerprint, validTimestamps)
      }
    }

    // Cleanup deduplication store
    for (const [fingerprint, entry] of this.deduplicationStore) {
      if (now - entry.lastSeen > this.DEDUPLICATION.windowMs) {
        this.deduplicationStore.delete(fingerprint)
        cleaned++
      }
    }

    // Enforce max cache sizes
    if (this.rateLimitStore.size > this.DEDUPLICATION.maxCacheSize) {
      const entries = Array.from(this.rateLimitStore.entries())
      entries.sort((a, b) => {
        const aLast = a[1][a[1].length - 1] ?? 0
        const bLast = b[1][b[1].length - 1] ?? 0
        return aLast - bLast
      }) // Sort by last timestamp
      const toDelete = entries.slice(0, entries.length - this.DEDUPLICATION.maxCacheSize)
      toDelete.forEach(([fingerprint]) => this.rateLimitStore.delete(fingerprint))
    }

    if (this.deduplicationStore.size > this.DEDUPLICATION.maxCacheSize) {
      const entries = Array.from(this.deduplicationStore.entries())
      entries.sort((a, b) => a[1].lastSeen - b[1].lastSeen) // Sort by last seen
      const toDelete = entries.slice(0, entries.length - this.DEDUPLICATION.maxCacheSize)
      toDelete.forEach(([fingerprint]) => this.deduplicationStore.delete(fingerprint))
    }

    this.log('debug', 'Store cleanup completed', { cleaned, rateLimitSize: this.rateLimitStore.size, dedupSize: this.deduplicationStore.size })
  }

  /**
   * Logging with observability
   */
  private log(level: 'debug' | 'info' | 'warn' | 'error', message: string, data?: Record<string, unknown>): void {
    const logMessage = `[ErrorMonitorV2] ${message}`
    
    switch (level) {
      case 'debug':
        console.debug(logMessage, data)
        break
      case 'info':
        console.info(logMessage, data)
        break
      case 'warn':
        console.warn(logMessage, data)
        break
      case 'error':
        console.error(logMessage, data)
        break
    }
  }

  /**
   * Utility methods
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private getCurrentStoryId(): string | undefined {
    if (typeof window === 'undefined') return undefined

    // Try sessionStorage first (if set via setStoryId)
    const storedId = sessionStorage.getItem('current_story_id')
    if (storedId) return storedId

    // Parse from URL: /stories/[uuid] or /stories/[uuid]/anything
    const match = window.location.pathname.match(/\/stories\/([a-f0-9-]{36})/i)
    return match?.[1] || undefined
  }

  /**
   * Get user ID synchronously (like getCurrentStoryId)
   * 
   * Pattern: Read directly from localStorage - no library dependencies
   * 1. Explicitly set userId (via setUser method)
   * 2. Supabase session from localStorage (direct read)
   * 
   * This ensures user_id is available even if error occurs before useEffect runs
   */
  private getUserId(): string | undefined {
    // First, try the explicitly set userId
    if (this.userId) return this.userId

    if (typeof window === 'undefined') return undefined

    try {
      // Read directly from localStorage - no Supabase client needed
      // Supabase stores session in localStorage with key pattern: sb-[project-ref]-auth-token

      // Find all localStorage keys that might contain Supabase session
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i)
        if (!key) continue

        // Look for Supabase auth token keys
        if (key.includes('sb-') && key.includes('-auth-token')) {
          const value = localStorage.getItem(key)
          if (!value) continue

          try {
            const parsed = JSON.parse(value)
            // Session structure: { user: { id: "..." }, ... }
            const userId = parsed?.user?.id
            if (userId) return userId
          } catch {
            // Not valid JSON, skip
            continue
          }
        }
      }
    } catch (error) {
      // Silently fail
    }

    return undefined
  }

  /**
   * Get deployment context (commit SHA, deployment ID, etc.)
   * 
   * Note: Client-side code can't access Vercel env vars directly.
   * Deployment context will be enhanced by the API route when storing errors.
   */
  private getDeploymentContext(): Record<string, unknown> {
    try {
      // Client-side: minimal deployment context
      // Full deployment info (commit SHA, deployment ID) will be added by API route
      if (typeof window !== 'undefined') {
        return {
          environment: 'client',
          timestamp: new Date().toISOString(),
          // Note: Full deployment context added by API route
        }
      }

      // Server-side: use process.env (though this won't run in client-side error monitoring)
      return {
        deployment_id: process.env['VERCEL_DEPLOYMENT_ID'] || 'unknown',
        git_commit_sha: process.env['VERCEL_GIT_COMMIT_SHA'] || 'unknown',
        git_commit_message: process.env['VERCEL_GIT_COMMIT_MESSAGE'] || 'unknown',
        git_branch: process.env['VERCEL_GIT_COMMIT_REF'] || 'unknown',
        deployed_at: process.env['VERCEL_DEPLOYMENT_CREATED_AT'] || new Date().toISOString(),
        environment: process.env['VERCEL_ENV'] || process.env['NODE_ENV'] || 'unknown'
      }
    } catch (error) {
      return {
        deployment_id: 'unavailable',
        error: 'Failed to get deployment context'
      }
    }
  }

  private getDeviceInfo() {
    if (typeof navigator === 'undefined') return {}
    
    const ua = navigator.userAgent
    return {
      platform: navigator.platform,
      browser: this.getBrowserName(ua),
      version: this.getBrowserVersion(ua),
      mobile: /Mobile|Android|iPhone|iPad/.test(ua)
    }
  }

  private getBrowserName(userAgent: string): string {
    if (userAgent.includes('Chrome')) return 'Chrome'
    if (userAgent.includes('Firefox')) return 'Firefox'
    if (userAgent.includes('Safari')) return 'Safari'
    if (userAgent.includes('Edge')) return 'Edge'
    return 'Unknown'
  }

  private getBrowserVersion(userAgent: string): string {
    const match = userAgent.match(/(?:Chrome|Firefox|Safari|Edge)\/(\d+)/)
    return match ? (match[1] || 'Unknown') : 'Unknown'
  }

  /**
   * Public API
   */
  setUser(userId: string, userTier?: 'basic' | 'premium'): void {
    this.userId = userId
  }

  setStoryId(storyId: string): void {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('current_story_id', storyId)
    }
  }

  isReady(): boolean {
    return this.isInitialized && !this.isDestroyed
  }

  getStatus(): {
    initialized: boolean
    destroyed: boolean
    circuitBreaker: CircuitBreakerState
    queueSize: number
    rateLimitSize: number
    deduplicationSize: number
  } {
    return {
      initialized: this.isInitialized,
      destroyed: this.isDestroyed,
      circuitBreaker: this.circuitBreaker,
      queueSize: this.errorQueue.length,
      rateLimitSize: this.rateLimitStore.size,
      deduplicationSize: this.deduplicationStore.size
    }
  }

  /**
   * Process queued errors (called when circuit closes)
   */
  processQueuedErrors(): void {
    this.processQueue()
  }
}

// Global instance
export const errorMonitorV2 = new ErrorMonitorV2()

// Export types for external use
export type { ErrorReport, ErrorSeverity, ErrorCategory, ObservabilityCallbacks }

// Export for window attachment
if (typeof window !== 'undefined') {
  interface WindowWithErrorMonitor extends Window {
    ErrorMonitor?: ErrorMonitorV2
    ErrorMonitorV2?: ErrorMonitorV2
  }
  ;(window as WindowWithErrorMonitor).ErrorMonitor = errorMonitorV2
  ;(window as WindowWithErrorMonitor).ErrorMonitorV2 = errorMonitorV2
}
